import { useState } from 'react';
import { auth, db } from '../../firebase';
import { useTheme } from '../../context/ThemeContext'; // Ajuste o caminho
import { 
  updatePassword, 
  reauthenticateWithCredential, 
  EmailAuthProvider,
  deleteUser,
  signOut 
} from 'firebase/auth';
import { doc, deleteDoc, collection, query, where, getDocs, writeBatch } from 'firebase/firestore';
import { FiStar, FiDollarSign, FiAlertTriangle, FiPhoneCall } from 'react-icons/fi';
import { FaAndroid, FaApple } from 'react-icons/fa';

const SettingsPanel = ({ 
  notificationSettings, 
  handleSettingChange, 
  copyToClipboard 
}) => {
  const [changePasswordModal, setChangePasswordModal] = useState(false);
  const [deleteAccountModal, setDeleteAccountModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [deleteConfirm, setDeleteConfirm] = useState('');

  const { isDarkMode, toggleTheme } = useTheme();

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('As senhas não coincidem!');
      setLoading(false);
      return;
    }

    if (passwordData.newPassword.length < 6) {
      alert('A nova senha deve ter pelo menos 6 caracteres!');
      setLoading(false);
      return;
    }

    try {
      const user = auth.currentUser;
      
      const credential = EmailAuthProvider.credential(
        user.email,
        passwordData.currentPassword
      );
      
      await reauthenticateWithCredential(user, credential);
      
      await updatePassword(user, passwordData.newPassword);
      
      alert('Senha alterada com sucesso!');
      setChangePasswordModal(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
    } catch (error) {
      console.error('Erro ao alterar senha:', error);
      
      if (error.code === 'auth/wrong-password') {
        alert('Senha atual incorreta!');
      } else if (error.code === 'auth/requires-recent-login') {
        alert('Por favor, faça login novamente antes de alterar a senha.');
      } else {
        alert('Erro ao alterar senha: ' + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm.toLowerCase() !== 'excluir minha conta') {
      alert('Por favor, digite "excluir minha conta" para confirmar.');
      return;
    }

    if (!window.confirm('⚠️ ATENÇÃO: Esta ação é irreversível!\n\nTem certeza absoluta que deseja excluir sua conta?')) {
      return;
    }

    setLoading(true);

    try {
      const user = auth.currentUser;
      
      const appsQuery = query(
        collection(db, 'apps'),
        where('userId', '==', user.uid)
      );
      
      const appsSnapshot = await getDocs(appsQuery);
      const batch = writeBatch(db);
      
      appsSnapshot.forEach((appDoc) => {
        batch.delete(appDoc.ref);
      });
      
      await batch.commit();
      
      await deleteDoc(doc(db, 'users', user.uid));
      
      await deleteUser(user);
      
      alert('Conta excluída com sucesso. Você será desconectado.');
      await signOut(auth);
      
    } catch (error) {
      console.error('Erro ao excluir conta:', error);
      
      if (error.code === 'auth/requires-recent-login') {
        alert('Por favor, faça login novamente antes de excluir a conta.');
      } else {
        alert('Erro ao excluir conta: ' + error.message);
      }
    } finally {
      setLoading(false);
      setDeleteAccountModal(false);
      setDeleteConfirm('');
    }
  };

  return (
    <div className="space-y-6">
      {/* NOVA SEÇÃO: Configurações de Tema */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
          Preferências de Tema
        </h3>
        
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-medium text-gray-800 dark:text-white">Modo Escuro</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {isDarkMode ? 'Tema escuro ativado' : 'Tema claro ativado'}
            </p>
          </div>
          <button
            onClick={toggleTheme}
            className={`
              relative inline-flex items-center h-6 rounded-full w-11
              transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
              ${isDarkMode ? 'bg-blue-600' : 'bg-gray-200'}
            `}
          >
            <span className="sr-only">Alternar tema</span>
            <span
              className={`
                inline-block w-4 h-4 transform bg-white rounded-full transition-transform
                ${isDarkMode ? 'translate-x-6' : 'translate-x-1'}
              `}
            />
          </button>
        </div>
      </div>

      {/* Configurações de Notificação */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Preferências de Notificação</h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-800 dark:text-white">Receber notificações</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">Permitir que enviemos notificações importantes</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                className="sr-only peer" 
                checked={notificationSettings.receiveNotifications}
                onChange={(e) => handleSettingChange('receiveNotifications', e.target.checked)}
                disabled
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 opacity-50"></div>
            </label>
          </div>
          
          <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
            <h4 className="font-medium text-gray-800 dark:text-white mb-2">Visibilidade do Aplicativo</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">Aumente a visibilidade dos seus aplicativos nas lojas</p>
            
            <div className="flex flex-wrap gap-3">
              <button 
                className={`px-4 py-2 rounded-lg text-sm font-medium ${notificationSettings.appVisibility === 'low' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'} opacity-50 cursor-not-allowed`}
                disabled
              >
                Baixa
              </button>
              <button 
                className={`px-4 py-2 rounded-lg text-sm font-medium ${notificationSettings.appVisibility === 'medium' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'} opacity-50 cursor-not-allowed`}
                disabled
              >
                Média
              </button>
              <button 
                className={`px-4 py-2 rounded-lg text-sm font-medium ${notificationSettings.appVisibility === 'high' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'} opacity-50 cursor-not-allowed`}
                disabled
              >
                Alta
              </button>
            </div>
          </div>

          <div className="bg-yellow-50 dark:bg-yellow-900 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mt-4">
            <div className="flex items-start">
              <FiStar className="text-yellow-600 dark:text-yellow-400 text-lg mr-2" />
              <div>
                <h4 className="font-medium text-yellow-800 dark:text-yellow-300">Recurso PRO</h4>
                <p className="text-sm text-yellow-700 dark:text-yellow-400">
                  As configurações de notificação estão disponíveis apenas para planos PRO. 
                  <a href="/pricing" className="underline ml-1">Atualize seu plano</a> para acessar estas funcionalidades.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Links de Download */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Links de Download</h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">Compartilhe esses links para que seus usuários possam baixar seu aplicativo</p>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <div className="flex items-center">
              <div className="bg-green-100 dark:bg-green-900 p-2 rounded-lg mr-3">
                <FaAndroid className="text-green-600 dark:text-green-400 w-5 h-5" />
              </div>
              <div>
                <h4 className="font-medium text-gray-800 dark:text-white">Google Play Store</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">Link para download na Play Store</p>
              </div>
            </div>
            <button 
              className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
              onClick={() => copyToClipboard('https://play.google.com/store/apps/details?id=com.seuapp')}
            >
              Copiar Link
            </button>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <div className="flex items-center">
              <div className="bg-blue-100 dark:bg-blue-900 p-2 rounded-lg mr-3">
                <FaApple className="text-blue-600 dark:text-blue-400 w-5 h-5" />
              </div>
              <div>
                <h4 className="font-medium text-gray-800 dark:text-white">Apple App Store</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">Link para download na App Store</p>
              </div>
            </div>
            <button 
              className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
              onClick={() => copyToClipboard('https://apps.apple.com/br/app/seu-app/id123456789')}
            >
              Copiar Link
            </button>
          </div>
        </div>
      </div>
      
      {/* Outras Configurações */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Outras Configurações</h3>
        
        <div className="space-y-4">
          <button 
            onClick={() => setChangePasswordModal(true)}
            className="w-full text-left p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <h4 className="font-medium text-gray-800 dark:text-white">Alterar Senha</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">Atualize sua senha de acesso</p>
          </button>
          
          <button className="w-full text-left p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors opacity-50 cursor-not-allowed">
            <h4 className="font-medium text-gray-800 dark:text-white">Privacidade e Segurança</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">Configure suas preferências de privacidade</p>
          </button>
          
          <button 
            onClick={() => setDeleteAccountModal(true)}
            className="w-full text-left p-3 rounded-lg border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900 transition-colors text-red-600 dark:text-red-400"
          >
            <h4 className="font-medium">Excluir Conta</h4>
            <p className="text-sm">Remover permanentemente sua conta e todos os dados</p>
          </button>
        </div>
      </div>

      {/* Modal de Alteração de Senha */}
      {changePasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg w-full max-w-md">
            <div className="p-6">
              <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">Alterar Senha</h3>
              
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Senha Atual
                  </label>
                  <input
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Nova Senha
                  </label>
                  <input
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
                    required
                    minLength={6}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Confirmar Nova Senha
                  </label>
                  <input
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
                    required
                  />
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setChangePasswordModal(false)}
                    className="px-4 py-2 text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? 'Alterando...' : 'Alterar Senha'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Exclusão de Conta */}
      {deleteAccountModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg w-full max-w-md">
            <div className="p-6">
              <div className="bg-red-100 dark:bg-red-900 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
                <h3 className="text-xl font-semibold text-red-800 dark:text-red-300 mb-2 flex items-center gap-2">
                  <FiAlertTriangle className="w-5 h-5" /> Atenção!
                </h3>
                <p className="text-red-700 dark:text-red-300 text-sm">
                  Esta ação é irreversível. Todos os seus aplicativos e dados serão permanentemente excluídos.
                </p>
              </div>

              <div className="bg-yellow-50 dark:bg-yellow-900 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-4">
                <h4 className="font-medium text-yellow-800 dark:text-yellow-300 mb-2 flex items-center gap-2">
                  <FiDollarSign className="w-5 h-5" /> Valores a Negociar
                </h4>
                <p className="text-yellow-700 dark:text-yellow-300 text-sm">
                  Se você possui valores investidos em aplicativos, entre em contato com nossa equipe 
                  para negociar possíveis reembolsos antes de excluir sua conta.
                </p>
                <p className="text-yellow-700 dark:text-yellow-300 text-sm mt-2 flex items-center gap-2">
                  <FiPhoneCall className="w-4 h-4" /> Contato: criaappoficial@gmail.com
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Digite "excluir minha conta" para confirmar:
                </label>
                <input
                  type="text"
                  value={deleteConfirm}
                  onChange={(e) => setDeleteConfirm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
                  placeholder="excluir minha conta"
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setDeleteAccountModal(false);
                    setDeleteConfirm('');
                  }}
                  className="px-4 py-2 text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={loading || deleteConfirm.toLowerCase() !== 'excluir minha conta'}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Excluindo...' : 'Excluir Conta'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPanel;